import { Check, X } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import Confetti from 'react-dom-confetti';
import { Modal } from '../Modal/Modal';
import LoginFlow from '../LoginFlow/LoginFlow';
import { BolderText } from '../BolderText/BolderText';

type PublicCatalogImportDialogsProps = {
  showSelection: boolean;
  onCloseSelection: () => void;
  onImport: () => void;
  isImporting: boolean;
  showLogin: boolean;
  onCloseLogin: () => void;
  onLoginSuccess: () => void;
  showSuccess: boolean;
  showConfetti: boolean;
  onCloseSuccess: () => void;
  onGoToOrders: () => void;
};

const CONFETTI_CONFIG = {
  angle: 90,
  spread: 360,
  startVelocity: 40,
  elementCount: 170,
  dragFriction: 0.12,
  duration: 10000,
  stagger: 3,
  width: '10px',
  height: '10px',
  perspective: '500px',
  colors: ['#f9c2c2', '#fef7f7', '#fda9a9', '#f39a9a', '#1b1b1a'],
};

const PublicCatalogImportDialogs = ({
  showSelection,
  onCloseSelection,
  onImport,
  isImporting,
  showLogin,
  onCloseLogin,
  onLoginSuccess,
  showSuccess,
  showConfetti,
  onCloseSuccess,
  onGoToOrders,
}: PublicCatalogImportDialogsProps) => {
  const { t } = useTranslation();

  const benefits = [
    { label: t('marketplace.catalogues.selection.benefit.orders.label'), desc: t('marketplace.catalogues.selection.benefit.orders.desc') },
    { label: t('marketplace.catalogues.selection.benefit.booking.label'), desc: t('marketplace.catalogues.selection.benefit.booking.desc') },
    { label: t('marketplace.catalogues.selection.benefit.calendar.label'), desc: t('marketplace.catalogues.selection.benefit.calendar.desc') },
    { label: t('marketplace.catalogues.selection.benefit.salon.label'), desc: t('marketplace.catalogues.selection.benefit.salon.desc') },
  ];

  return (
    <>
      {showSelection && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 p-3 sm:p-6"
          onClick={onCloseSelection}
          role="presentation"
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="catalog-import-title"
            className="relative w-full max-w-lg max-h-[90dvh] overflow-y-auto rounded-2xl bg-card shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={onCloseSelection}
              aria-label={t('marketplace.catalogues.lookbook.close')!}
              className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/10 text-foreground transition hover:bg-black/20 dark:bg-white/10 dark:hover:bg-white/20"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="space-y-6 px-5 py-8 sm:px-8">
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-as-pink-500">
                  {t('marketplace.catalogues.lookbook.partner_kicker')}
                </p>
                <h2 id="catalog-import-title" className="text-2xl font-semibold text-foreground">
                  {t('marketplace.catalogues.selection.title')}
                </h2>
                <p className="text-base leading-relaxed text-muted-foreground">
                  {t('marketplace.catalogues.selection.intro')}
                </p>
              </div>
              <ul className="grid gap-3">
                {benefits.map((benefit) => (
                  <li key={benefit.label} className="rounded-xl border border-as-pink-200 bg-white/80 p-4 dark:bg-white/10">
                    <div className="flex items-start gap-2">
                      <Check className="mt-0.5 h-5 w-5 shrink-0 text-as-pink-400" />
                      <p className="text-sm leading-relaxed text-muted-foreground">
                        <b>{benefit.label}</b> {benefit.desc}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={onImport}
                disabled={isImporting}
                className="inline-flex h-12 w-full items-center justify-center rounded-full bg-as-pink-300 px-6 text-sm font-semibold text-white btn-hover-motion hover:bg-as-pink-500 disabled:cursor-not-allowed disabled:bg-gray-400"
              >
                {isImporting ? t('global.loading') : t('marketplace.catalogues.lookbook.import')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showLogin && (
        <Modal canClose modalTitle={t('guest.login.form.title')} onClose={onCloseLogin}>
          <>
            <p>{t('guest.login.form.description')}.</p>
            <ul className="mb-6 mt-4 space-y-3 text-left">
              <li className="flex items-start">
                <Check className="mr-2 mt-1 h-5 w-5 shrink-0 text-gold-500" />
                <BolderText text={t('guest.login.form.reason_1')!} />
              </li>
              <li className="flex items-start">
                <Check className="mr-2 mt-1 h-5 w-5 shrink-0 text-gold-500" />
                <BolderText text={t('guest.login.form.reason_2')!} />
              </li>
              <li className="flex items-start">
                <Check className="mr-2 mt-1 h-5 w-5 shrink-0 text-gold-500" />
                <BolderText text={t('guest.login.form.reason_3')!} />
              </li>
              <li className="flex items-start">
                <Check className="mr-2 mt-1 h-5 w-5 shrink-0 text-gold-500" />
                <span>... {t('guest.login.form.reason_others')}</span>
              </li>
            </ul>
            <LoginFlow callbackFn={onLoginSuccess} skipOnboardingAfterSignup={true} />
          </>
        </Modal>
      )}

      {showSuccess && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 p-3 sm:p-6"
          onClick={onCloseSuccess}
          role="presentation"
        >
          <div
            role="dialog"
            aria-modal="true"
            className="relative w-full max-w-md rounded-2xl bg-card p-8 text-center shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex justify-center">
              <Confetti active={showConfetti} config={CONFETTI_CONFIG} />
            </div>
            <h2 className="text-2xl font-semibold text-foreground">{t('marketplace.import.title')}</h2>
            <p className="mt-3 text-muted-foreground">
              <Trans
                i18nKey="marketplace.import.body"
                components={{ bold: <span className="font-semibold text-foreground" /> }}
              />
            </p>
            <button
              type="button"
              onClick={onGoToOrders}
              className="mt-6 inline-flex h-12 w-full items-center justify-center rounded-full bg-as-pink-300 px-6 text-sm font-semibold text-white btn-hover-motion hover:bg-as-pink-500"
            >
              {t('marketplace.import.cta.order')}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default PublicCatalogImportDialogs;
